import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Upload, Save, Loader2, Trash2, AlertTriangle, ListPlus, RefreshCw, Info,
} from 'lucide-react';
import { salesTargetsAPI, incentiveAPI } from '../../services/api';
import SalesSheetDownloadButton from '../../components/SalesSheetDownloadButton';
import { SearchInput } from '../../components/SearchInput';
import PaginationFooter from '../../components/PaginationFooter';
import { usePagination } from '../../hooks/usePagination';
import { PageErrorBanner } from '../../components/PageErrorBanner';
import { BlockLoader } from '../../components/loading';
import SalesTargetUploadModal from '../../components/SalesTargetUploadModal';
import SalesFieldsModal from '../../components/SalesFieldsModal';
import ConfirmDeleteModal from '../../components/ConfirmDeleteModal';
import Modal from '../../components/Modal';
import SalesCasesModal from '../../components/SalesCasesModal';
import NoLongerDeployedBadge from '../../components/NoLongerDeployedBadge';
import {
  coordKey, formatNumber,
  type CoordinateOption, type SalesTargetConfig, type Scope, type SalesSource,
} from './types';

/**
 * The Targets grid and the Sales grid — one component, because they are the same
 * screen.
 *
 * Both are "a list of stores (or promoters), one editable number each, for one
 * product, for one month". The differences are two strings and one extra button,
 * and building them twice would guarantee they drift: a fix to the dirty-cell
 * handling or the save batching would land on one and not the other.
 *
 * ── WHY ONE PRODUCT AT A TIME ────────────────────────────────────────────────
 * A project with 900 stores and 50 products has 45,000 cells. Rendering that as
 * a wide grid is unusable and unreadable — you cannot scan a row of 50 numbers
 * and you certainly cannot find the wrong one. The bulk path for that shape is
 * the spreadsheet, which is exactly what a spreadsheet is good at. This grid is
 * for the other job: correcting a handful of figures, where one product and a
 * search box is the fastest possible route to the cell you want.
 */

interface Props {
  mode: 'target' | 'sales';
  projectId: number;
  month: string;
  config: SalesTargetConfig;
  /** Lifts a settings change made from this tab back to the page. */
  onConfigChange: (config: SalesTargetConfig) => void;
  /** Opens the Results tab, where the DERIVED side of the book is shown. */
  onOpenResults: () => void;
}

interface SubjectRow {
  key: string;
  code: string;
  name: string;
  /** Headcount for a store, days deployed for a promoter. */
  detail: string;
  /** Category / trade / city, or designation / store — enough to tell two apart. */
  context: string;
  /**
   * Promoters only. The list is everyone deployed at any point in the month, so a
   * past month still has the people who have left since — false marks them.
   */
  stillDeployed: boolean;
  deployedUntil: string | null;
}

interface ValueRow {
  id: string;
  subjectKey: string;
  value: number;
  source: string;
}

const PAGE_SIZES = [
  { label: '25', value: 25 },
  { label: '50', value: 50 },
  { label: '100', value: 100 },
];

/** Compact labelled segmented control for the two per-tab settings. */
function Segmented({ label, value, options, onChange, disabled }: {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs font-medium text-gray-600">{label}</span>
      <div className="flex rounded-lg border border-gray-200 bg-white p-0.5">
        {options.map((o) => (
          <button
            key={o.value}
            type="button"
            disabled={disabled}
            onClick={() => { if (o.value !== value) onChange(o.value); }}
            className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors disabled:opacity-50 ${
              value === o.value ? 'bg-primary-50 text-primary-700' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function GridTab({ mode, projectId, month, config, onConfigChange, onOpenResults }: Props) {
  const isSales = mode === 'sales';
  const scope: Scope = isSales ? config.salesScope : config.sourceScope;
  const noun = isSales ? 'sales' : 'targets';
  // Two ways the Sales tab becomes read-only, for different reasons.
  // 'running_stock' — the figures come from task answers.
  // 'detailed'      — the figures are the sum of per-case rows, which are
  //                   uploaded as their own sheet because each carries its own
  //                   dates and details.
  const fromStock = isSales && config.salesSource === 'running_stock';
  const fromLines = isSales && config.salesSource === 'detailed';
  const derived = fromStock || fromLines;

  const [coordinates, setCoordinates] = useState<CoordinateOption[]>([]);
  const [selectedCoord, setSelectedCoord] = useState<string>('');
  const [subjects, setSubjects] = useState<SubjectRow[]>([]);
  const [values, setValues] = useState<Map<string, ValueRow>>(new Map());
  // Only the cells the admin actually touched. Saving the whole grid would
  // rewrite every row's `source` and `updated_by` on a one-cell correction, and
  // make the audit trail useless.
  const [dirty, setDirty] = useState<Map<string, string>>(new Map());
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [fieldsOpen, setFieldsOpen] = useState(false);
  const [clearOpen, setClearOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [savingSetting, setSavingSetting] = useState(false);
  /** The promoter whose individual sales are open, on per-sale projects. */
  const [casesFor, setCasesFor] = useState<string | null>(null);
  /** A scope switch that would orphan figures already entered, awaiting a yes. */
  const [pendingScope, setPendingScope] = useState<{ next: Scope; existing: number } | null>(null);

  /**
   * Change one of this tab's own settings without leaving it.
   *
   * These live in the Settings modal too, but "where do sales come from" is a
   * decision you make while looking at the Sales tab, and burying it four
   * sections down behind a page-level button meant nobody could find it.
   */
  /**
   * ⚠️ Switching which side OWNS the figures is not a view change.
   *
   * It repoints every calculation in the module. Somebody who has uploaded 2,488
   * store targets and then clicks "The promoter" — reasonably expecting to see
   * what each promoter got — instead makes those 2,488 stop being used, silently,
   * because this tab shows what was ENTERED and the entered figures are on the
   * other side. So a switch that would orphan data asks first, and says how much.
   */
  const requestScopeChange = async (next: Scope) => {
    if (next === scope) return;
    setError(null);
    try {
      const res = isSales
        ? await salesTargetsAPI.listSales(projectId, { month, scope })
        : await salesTargetsAPI.listTargets(projectId, { month, scope });
      const rows = isSales ? (res.data.data.rows ?? []) : (res.data.data ?? []);
      if (rows.length > 0) {
        setPendingScope({ next, existing: rows.length });
        return;
      }
    } catch {
      // If the count cannot be fetched, ask anyway rather than switch silently.
      setPendingScope({ next, existing: 0 });
      return;
    }
    await saveSetting(isSales ? { salesScope: next } : { sourceScope: next });
  };

  const saveSetting = async (patch: Partial<SalesTargetConfig>) => {
    setSavingSetting(true);
    setError(null);
    try {
      const res = await salesTargetsAPI.updateConfig(projectId, patch as never);
      onConfigChange(res.data.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Could not change that setting.');
    } finally {
      setSavingSetting(false);
    }
  };

  // `keepScreen` reloads behind what is on screen. The loading screen replaces
  // the whole tab, the upload modal with it — so a reload after an upload used to
  // throw away the result the modal had just been given, and nobody saw it.
  const load = useCallback(async (opts?: { keepScreen?: boolean }) => {
    if (!opts?.keepScreen) setLoading(true);
    setError(null);
    try {
      const [coordRes, subjectRes, valueRes] = await Promise.all([
        salesTargetsAPI.listCoordinates(projectId, config.productGranularity),
        // The subject list is asked for in THIS tab's scope. A project can own
        // its targets at store level and its sales at employee level, so reusing
        // one list for both would put the wrong rows on one of the two tabs.
        salesTargetsAPI.listSubjects(projectId, month, scope, mode),
        fromStock
          ? salesTargetsAPI.getRunningStockSales(projectId, month)
          : fromLines
            ? incentiveAPI.listDetailed(projectId, month)
            : isSales
              ? salesTargetsAPI.listSales(projectId, { month, scope })
              : salesTargetsAPI.listTargets(projectId, { month, scope }),
      ]);

      const coords: CoordinateOption[] = coordRes.data.data ?? [];
      setCoordinates(coords);
      setSelectedCoord((prior) => (
        prior && coords.some((c) => coordKey(c.coord) === prior) ? prior
          : coords.length > 0 ? coordKey(coords[0].coord) : ''
      ));

      setSubjects((subjectRes.data.data.rows ?? []).map((r: any): SubjectRow => ({
        key: r.key,
        code: r.code,
        name: r.name,
        detail: scope === 'store'
          ? `${r.headcount} promoter${r.headcount === 1 ? '' : 's'}`
          : `${r.deployedDays} day${r.deployedDays === 1 ? '' : 's'} deployed`,
        context: r.detail ?? '',
        stillDeployed: r.stillDeployed !== false,
        deployedUntil: r.deployedUntil ?? null,
      })));

      const map = new Map<string, ValueRow>();
      if (derived) {
        // Both derived shapes arrive as a flat list of quantities; the monthly
        // figure is their sum per subject and product.
        //
        // ⚠️ For per-sale data read `totals`, NEVER `rows`. `rows` is a capped
        // page for display — a month with 54,000 sales sends 5,000 of them, and
        // summing those would understate every promoter. `totals` is grouped in
        // SQL and is complete.
        const source = fromLines
          ? (valueRes.data.data.totals ?? [])
          : (valueRes.data.data.rows ?? []);
        source.forEach((r: any) => {
          const subjectKey = scope === 'store'
            ? r.storeId
            : r.employeeCode;
          if (!subjectKey) return;
          const productId = r.productId ?? r.coord?.productId;
          const skuId = r.skuId ?? r.coord?.skuId ?? '';
          if (!productId) return;
          const key = `${subjectKey}::${productId}::${skuId ?? ''}`;
          const prior = map.get(key);
          map.set(key, {
            id: key, subjectKey,
            value: (prior?.value ?? 0) + Number(r.quantity ?? 0),
            source: fromStock ? 'running_stock' : 'detailed',
          });
        });
      } else {
        const list = isSales ? (valueRes.data.data.rows ?? []) : (valueRes.data.data ?? []);
        list.forEach((r: any) => {
          const subjectKey = scope === 'store' ? r.storeId : r.employeeCode;
          if (!subjectKey) return;
          map.set(`${subjectKey}::${coordKey(r.coord)}`, {
            id: r.id, subjectKey,
            value: Number(isSales ? r.soldValue : r.targetValue),
            source: r.source,
          });
        });
      }
      setValues(map);
      setDirty(new Map());
    } catch (err: any) {
      setError(err.response?.data?.message || `Could not load ${noun} for ${month}.`);
    } finally {
      setLoading(false);
    }
  }, [projectId, month, scope, mode, config.productGranularity, derived, fromStock, fromLines, isSales, noun]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => { setPage(1); }, [search, selectedCoord]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return subjects;
    return subjects.filter((s) => s.code.toLowerCase().includes(q) || s.name.toLowerCase().includes(q));
  }, [subjects, search]);

  const { totalPages, safePage, paginatedData } = usePagination(filtered, page, pageSize);

  // The product on screen, offered to the sheet download as "only this product".
  // Download and Upload themselves are never per product — see SalesSheetDownloadButton.
  const sheetProduct = useMemo(() => {
    const option = coordinates.find((c) => coordKey(c.coord) === selectedCoord);
    return option ? { key: selectedCoord, label: option.label } : null;
  }, [coordinates, selectedCoord]);

  const cellKey = (subjectKey: string) => `${subjectKey}::${selectedCoord}`;
  const storedValue = (subjectKey: string) => values.get(cellKey(subjectKey));
  const displayValue = (subjectKey: string): string => {
    const k = cellKey(subjectKey);
    if (dirty.has(k)) return dirty.get(k)!;
    const v = values.get(k);
    return v === undefined ? '' : String(v.value);
  };

  const setCell = (subjectKey: string, raw: string) => {
    const next = new Map(dirty);
    const k = cellKey(subjectKey);
    const stored = values.get(k);
    // Typing a value back to what it already was un-dirties the cell, so the
    // Save button honestly reflects whether anything would change.
    if ((stored === undefined && raw.trim() === '') || (stored !== undefined && raw.trim() === String(stored.value))) {
      next.delete(k);
    } else {
      next.set(k, raw);
    }
    setDirty(next);
  };

  const invalidCells = useMemo(() => {
    const bad: string[] = [];
    dirty.forEach((raw, k) => {
      const trimmed = raw.trim();
      if (trimmed === '') return; // clearing is handled below, not an error
      const n = Number(trimmed);
      if (!Number.isFinite(n) || n < 0) bad.push(k);
    });
    return bad;
  }, [dirty]);

  const save = async () => {
    if (dirty.size === 0) return;
    if (invalidCells.length > 0) {
      setError('Some figures are not valid numbers. A figure must be zero or more.');
      return;
    }
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      if (!coordinates.some((c) => coordKey(c.coord) === selectedCoord)) throw new Error('Choose a product first.');

      const rows: any[] = [];
      const deletions: string[] = [];
      dirty.forEach((raw, k) => {
        // Each edit belongs to the product it was typed under, which is in its
        // key — NOT to whichever product the box shows when Save is pressed. Read
        // from the box, a figure typed for one product and saved after switching
        // was written against the other.
        const coord = coordinates.find((c) => k.endsWith(`::${coordKey(c.coord)}`));
        if (!coord) return;
        const subjectKey = k.slice(0, k.length - coordKey(coord.coord).length - 2);
        const trimmed = raw.trim();
        if (trimmed === '') {
          // An emptied cell REMOVES the figure rather than storing zero. Zero is
          // a real target of nothing; blank means "never set", and the two lead
          // to different behaviour once defaults are switched on.
          // Derived rows have no database id to delete — that view is read-only
          // and never reaches here.
          const stored = values.get(k);
          if (stored && !derived) deletions.push(stored.id);
          return;
        }
        const base = {
          productId: coord.coord.productId,
          skuId: coord.coord.skuId,
          ...(scope === 'store' ? { storeId: subjectKey } : { employeeCode: subjectKey }),
        };
        rows.push(isSales ? { ...base, soldValue: Number(trimmed) } : { ...base, targetValue: Number(trimmed) });
      });

      if (rows.length > 0) {
        if (isSales) await salesTargetsAPI.saveSales(projectId, { month, scope, sales: rows });
        else await salesTargetsAPI.saveTargets(projectId, { month, scope, targets: rows });
      }
      for (const id of deletions) {
        if (isSales) await salesTargetsAPI.deleteSales(projectId, id);
        else await salesTargetsAPI.deleteTarget(projectId, id);
      }
      setNotice(
        `${rows.length + deletions.length} figure${rows.length + deletions.length === 1 ? '' : 's'} saved.`,
      );
      await load();
    } catch (err: any) {
      setError(err.response?.data?.message || `Could not save the ${noun}.`);
    } finally {
      setSaving(false);
    }
  };

  const importFromStock = async () => {
    setImporting(true);
    setError(null);
    try {
      const res = await salesTargetsAPI.importRunningStockSales(projectId, month);
      setNotice(res.data.message ?? 'Imported.');
      await load();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Could not import from Running Stock.');
    } finally {
      setImporting(false);
    }
  };

  const clearMonth = async () => {
    setError(null);
    try {
      if (fromLines) await incentiveAPI.clearDetailed(projectId, month);
      else if (isSales) await salesTargetsAPI.clearSalesMonth(projectId, month, scope);
      else await salesTargetsAPI.clearTargetMonth(projectId, month, scope);
      setClearOpen(false);
      setNotice(`Every ${scope} ${noun} figure for ${month} was removed.`);
      await load();
    } catch (err: any) {
      setError(err.response?.data?.message || `Could not clear ${month}.`);
    }
  };

  const setCount = useMemo(() => {
    let n = 0;
    values.forEach((v, k) => { if (k.endsWith(`::${selectedCoord}`) && v) n += 1; });
    return n;
  }, [values, selectedCoord]);

  /** Promoters on this month's list who have since left the project. */
  const noLongerDeployedCount = useMemo(
    () => subjects.filter((s) => !s.stillDeployed).length,
    [subjects],
  );

  if (loading) {
    return (
      <BlockLoader className="rounded-xl border border-gray-200 bg-white py-20" />
    );
  }

  if (coordinates.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-gray-300 bg-white py-16 text-center">
        <h3 className="text-lg font-medium text-gray-600">No products yet</h3>
        <p className="mx-auto mt-2 max-w-md text-sm text-gray-500">
          {noun === 'sales' ? 'Sales' : 'Targets'} are set per product, and this project&apos;s
          Product Master is empty. Add the products in Master Data → Products and they will
          appear here.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {error && <PageErrorBanner message={error} />}
      {notice && (
        <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
          {notice}
        </div>
      )}

      {/* How this tab works — the two settings that decide what it asks for.
          Also in Settings, but this is where the decision is actually made. */}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 rounded-lg border border-gray-200 bg-gray-50 px-4 py-2.5">
        <Segmented
          label={isSales ? 'Sales are recorded against' : 'Targets are set by'}
          value={scope}
          disabled={savingSetting}
          onChange={(v) => { void requestScopeChange(v as Scope); }}
          options={[
            { value: 'store', label: 'The store' },
            { value: 'employee', label: 'The promoter' },
          ]}
        />
        {isSales && (
          <Segmented
            label="Sales come from"
            value={config.salesSource}
            disabled={savingSetting}
            onChange={(v) => saveSetting({ salesSource: v as SalesSource })}
            options={[
              { value: 'upload', label: 'Monthly upload' },
              { value: 'detailed', label: 'One row per sale' },
              { value: 'running_stock', label: 'The stock tasks' },
            ]}
          />
        )}
        {savingSetting && <Loader2 className="h-4 w-4 animate-spin text-gray-400" />}
      </div>

      {/* The question this answers is the one the scope control invites: "I set
          these by store — what does each promoter get?" The answer is a different
          tab, and saying so here is cheaper than letting someone flip the setting
          to look for it. */}
      <p className="text-xs text-gray-500">
        This tab shows what has been entered. The{' '}
        {scope === 'store' ? 'promoter' : 'store'} side is worked out from it —{' '}
        <button
          type="button"
          onClick={onOpenResults}
          className="font-medium text-primary-600 underline underline-offset-2 hover:text-primary-700"
        >
          see the Results tab
        </button>
        .
      </p>

      {fromStock && (
        <div className="flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
          <Info className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <div>
            <p className="font-medium">Sales come from the stock tasks, so this view is read-only.</p>
            <p className="mt-1 text-blue-800">
              Every answer whose question records units leaving the shelf counts as a sale, and the
              answer carries the promoter who recorded it — so nothing has to be shared out by a
              multiplier. Import them below if you need to adjust a figure by hand afterwards, or
              change the source above to record sales by upload instead.
            </p>
          </div>
        </div>
      )}

      {fromLines && (
        <div className="flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
          <Info className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <div>
            <p className="font-medium">
              Sales are recorded one row per sale, so these monthly figures are their sum.
            </p>
            <p className="mt-1 text-blue-800">
              Download the sheet below, put one line per sale on it — with its own dates and details
              — and upload it back. That is what lets an incentive rule ask about an individual
              sale, which a monthly total cannot answer. Uploads add up, so a month can be sent in
              parts; a sale sent again under the same Reference is updated, not counted twice.
              Choose a Replace option when uploading only for a file holding the promoters&apos; whole month,
              of every product or of the products it carries.{' '}
              <strong>Click a promoter to see their individual sales</strong> — each with its dates
              and whether it counted towards incentive.
            </p>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600" htmlFor="st-product">
              Product
            </label>
            <select
              id="st-product"
              value={selectedCoord}
              onChange={(e) => setSelectedCoord(e.target.value)}
              className="min-w-[16rem] h-[38px] rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
            >
              {coordinates.map((c) => (
                <option key={coordKey(c.coord)} value={coordKey(c.coord)}>{c.label}</option>
              ))}
            </select>
          </div>
          <div className="w-64">
            <SearchInput
              size="md"
              value={search}
              onChange={setSearch}
              placeholder={scope === 'store' ? 'Search store code or name' : 'Search employee code or name'}
            />
          </div>
          <p className="pb-2 text-xs text-gray-500">
            {setCount} of {subjects.length} {scope === 'store' ? 'stores' : 'promoters'} set
            {scope === 'employee' && noLongerDeployedCount > 0 && (
              <span title={`Everyone deployed at any point in ${month} is listed, including those who have left since`}>
                {' '}· {noLongerDeployedCount} no longer deployed
              </span>
            )}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {isSales && (
            <button
              type="button"
              onClick={() => setFieldsOpen(true)}
              className="flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <ListPlus className="h-4 w-4" />
              Sales details
            </button>
          )}
          {fromStock ? (
            <button
              type="button"
              onClick={importFromStock}
              disabled={importing}
              className="flex items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-60"
            >
              {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              Import into Sales
            </button>
          ) : fromLines ? (
            <>
              {/* No download button here: the upload modal opens on the per-sale
                  sheet, so a second one beside it is the same click twice. */}
              <button
                type="button"
                onClick={() => setUploadOpen(true)}
                className="flex items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-2 text-sm font-semibold text-white hover:bg-primary-700"
              >
                <Upload className="h-4 w-4" />
                Upload per-sale rows
              </button>
              <button
                type="button"
                onClick={() => setClearOpen(true)}
                className="flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50"
              >
                <Trash2 className="h-4 w-4" />
                Clear month
              </button>
            </>
          ) : (
            <>
              <SalesSheetDownloadButton
                projectId={projectId}
                month={month}
                sheet={mode}
                scope={scope}
                product={sheetProduct}
                productCount={coordinates.length}
                label="Download sheet"
                onError={setError}
              />
              <button
                type="button"
                onClick={() => setUploadOpen(true)}
                className="flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <Upload className="h-4 w-4" />
                Upload
              </button>
              <button
                type="button"
                onClick={() => setClearOpen(true)}
                className="flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50"
              >
                <Trash2 className="h-4 w-4" />
                Clear month
              </button>
              <button
                type="button"
                onClick={save}
                disabled={dirty.size === 0 || saving}
                className="flex items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Save{dirty.size > 0 ? ` (${dirty.size})` : ''}
              </button>
            </>
          )}
        </div>
      </div>

      {/* The buttons sit beside the Product box, which reads as one file per
          product. It never was: say what the box does and what the files carry. */}
      {!fromStock && coordinates.length > 1 && (
        <p className="text-xs text-gray-500">
          {fromLines
            ? 'One upload takes every product: the Product column on each row of the per-sale sheet says which. '
              + 'The Product box picks the product shown here; an upload is never limited to it.'
            : `Download sheet and Upload carry every product in one file${
              sheetProduct ? `, or only ${sheetProduct.label} if you choose that at download` : ''
            }. The Product box picks the product this table shows and edits; an upload is never limited to it.`}
        </p>
      )}

      {invalidCells.length > 0 && (
        <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-800">
          <AlertTriangle className="h-4 w-4" />
          {invalidCells.length} figure{invalidCells.length === 1 ? '' : 's'} {invalidCells.length === 1 ? 'is' : 'are'} not
          a number of zero or more.
        </div>
      )}

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                <th className="px-4 py-3">{scope === 'store' ? 'Store' : 'Promoter'}</th>
                <th className="px-4 py-3">Name</th>
                <th className="w-40 px-4 py-3 text-right">
                  {isSales ? 'Sold' : 'Monthly target'}
                </th>
                <th className="w-32 px-4 py-3">Set by</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {paginatedData.map((s) => {
                const stored = storedValue(s.key);
                const k = cellKey(s.key);
                const isDirty = dirty.has(k);
                return (
                  <tr
                    key={s.key}
                    // On a per-sale project a row is a SUMMARY of individual sales —
                    // clicking opens them, with their dates and whether each counted.
                    className={isDirty ? 'bg-amber-50/60'
                      : fromLines && scope === 'employee' ? 'cursor-pointer hover:bg-gray-50' : 'hover:bg-gray-50'}
                    onClick={fromLines && scope === 'employee' ? () => setCasesFor(s.key) : undefined}
                    title={fromLines && scope === 'employee' ? 'See the individual sales' : undefined}
                  >
                    <td className="px-4 py-2">
                      <span className="font-medium text-gray-900">{s.code}</span>
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex flex-wrap items-center gap-2 text-gray-900">
                        {s.name}
                        <NoLongerDeployedBadge stillDeployed={s.stillDeployed} deployedUntil={s.deployedUntil} />
                      </div>
                      <div className="text-xs text-gray-500">
                        {[s.detail, s.context].filter(Boolean).join(' · ')}
                      </div>
                    </td>
                    <td className="px-4 py-2 text-right">
                      {derived ? (
                        <span className="font-medium text-gray-900">
                          {formatNumber(stored?.value ?? 0)}
                        </span>
                      ) : (
                        <input
                          type="number"
                          min={0}
                          step="any"
                          inputMode="decimal"
                          value={displayValue(s.key)}
                          onChange={(e) => setCell(s.key, e.target.value)}
                          placeholder="—"
                          className="w-28 rounded-lg border border-gray-300 px-2 py-1 text-right text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
                        />
                      )}
                    </td>
                    <td className="px-4 py-2">
                      {stored ? (
                        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                          {stored.source === 'running_stock' ? 'Stock task'
                            : stored.source === 'detailed' ? 'Per-sale rows'
                              : stored.source === 'upload' ? 'Upload' : 'By hand'}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">Not set</span>
                      )}
                    </td>
                  </tr>
                );
              })}
              {paginatedData.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-4 py-12 text-center text-sm text-gray-500">
                    {subjects.length === 0
                      ? scope === 'store'
                        ? 'No active stores in this project yet.'
                        : `No promoters were deployed in this project in ${month}.`
                      : 'Nothing matches that search.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {filtered.length > 0 && (
          <PaginationFooter
            safePage={safePage}
            totalPages={totalPages}
            pageSize={pageSize}
            pageSizeOptions={PAGE_SIZES}
            totalRows={filtered.length}
            onPageSizeChange={(v) => { setPageSize(v); setPage(1); }}
            onPrevPage={() => setPage((p) => Math.max(1, p - 1))}
            onNextPage={() => setPage((p) => Math.min(totalPages, p + 1))}
          />
        )}
      </div>

      <SalesCasesModal
        isOpen={casesFor !== null}
        onClose={() => setCasesFor(null)}
        projectId={projectId}
        month={month}
        employeeCode={casesFor}
        productId={coordinates.find((c) => coordKey(c.coord) === selectedCoord)?.coord.productId ?? null}
        skuId={coordinates.find((c) => coordKey(c.coord) === selectedCoord)?.coord.skuId ?? null}
        productLabel={coordinates.find((c) => coordKey(c.coord) === selectedCoord)?.label ?? null}
      />

      <Modal
        isOpen={pendingScope !== null}
        onClose={() => setPendingScope(null)}
        title={`Change who owns the ${noun}?`}
        size="lg"
        footer={
          <div className="flex w-full justify-end gap-2">
            <button
              type="button"
              onClick={() => setPendingScope(null)}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Keep it as it is
            </button>
            <button
              type="button"
              onClick={async () => {
                const next = pendingScope!.next;
                setPendingScope(null);
                await saveSetting(isSales ? { salesScope: next } : { sourceScope: next });
              }}
              className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700"
            >
              Switch anyway
            </button>
          </div>
        }
      >
        {pendingScope && (
          <div className="space-y-3 text-sm text-gray-700">
            <p>
              This is a setting, not a way of looking at the same numbers. It changes which side
              of the book this project keeps, and the other side is worked out from it.
            </p>
            {pendingScope.existing > 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-amber-900">
                <strong>{pendingScope.existing.toLocaleString()}</strong>{' '}
                {scope === 'store' ? 'store' : 'promoter'} figure
                {pendingScope.existing === 1 ? ' is' : 's are'} already recorded for {month}.
                Switching to {pendingScope.next === 'store' ? 'store' : 'promoter'}-owned means
                they stop being used in every calculation. Nothing is deleted — switching back
                restores them.
              </div>
            )}
            <p className="text-gray-600">
              If you only wanted to see what each {scope === 'store' ? 'promoter' : 'store'} ends
              up with, that is the Results tab — no setting needs to change.
            </p>
          </div>
        )}
      </Modal>

      <SalesTargetUploadModal
        isOpen={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onSuccess={() => { void load({ keepScreen: true }); }}
        projectId={projectId}
        month={month}
        scope={scope}
        mode={fromLines ? 'sales-detailed' : mode}
        product={sheetProduct}
        productCount={coordinates.length}
      />

      <SalesFieldsModal
        isOpen={fieldsOpen}
        onClose={() => setFieldsOpen(false)}
        projectId={projectId}
      />

      <ConfirmDeleteModal
        isOpen={clearOpen}
        onClose={() => setClearOpen(false)}
        onConfirm={clearMonth}
        isSubmitting={saving}
        title={`Clear ${month}?`}
        entityLabel={`every ${scope === 'store' ? 'store' : 'promoter'} ${noun} figure`}
        entityName={month}
        consequenceNote={
          'This cannot be undone. Download the sheet first if you want a copy of what is there now.'
        }
        confirmLabel="Clear the month"
      />
    </div>
  );
}
